import { ReactNode, useEffect, useRef, useState } from "react";
import { Box, BoxProps } from "@chakra-ui/react";
import throttle from "lodash.throttle";
import { Shadow } from "./shadow";

interface IScrollShadowIndicatorProps extends BoxProps {
    // Number of pixels when the blur state is on
    threshold?: number;
    children?: ReactNode | null;
    shadowProps?: BoxProps;
}

const THROTTLE_TIME = 200;

export const ScrollShadowIndicator = ({ threshold = 30, children, shadowProps, ...boxProps }: IScrollShadowIndicatorProps) => {
    const ref = useRef<HTMLDivElement>(null);
    const [shouldShow, setShowShadow] = useState<boolean>(true);

    const onScroll = throttle((event: React.UIEvent<HTMLDivElement>) => {
        const element = event.target as Element;
        const shouldApplyShadow = element.scrollTop > threshold || !(element.scrollHeight !== element.clientHeight);

        setShowShadow(!shouldApplyShadow);
    }, THROTTLE_TIME);

    const checkOverflowScroll = () => {
        const element = ref.current;

        if (element) {
            const overflowScroll = element.scrollHeight > element.offsetHeight;

            setShowShadow(Boolean(overflowScroll));
        }
    };

    useEffect(() => {
        checkOverflowScroll();

        window.addEventListener("scroll", checkOverflowScroll);
        window.addEventListener("resize", checkOverflowScroll);

        return () => {
            window.removeEventListener("scroll", checkOverflowScroll);
            window.removeEventListener("resize", checkOverflowScroll);
        };
    }, []);

    return (
        <>
            <Box ref={ref} onScroll={onScroll} overflowY="auto" {...boxProps}>
                {children}
            </Box>

            <Shadow shouldShow={shouldShow} shadowProps={shadowProps} />
        </>
    );
};
